import { Avatar, Badge, Button, Chip, CircularProgress } from "@mui/material";
import Flex from "@/components/Flex";
import { ScrollArea } from "@/components/ScrollBar";
import Text from "@/components/Text";
import CustomTitle from "@/components/app/CustomTitle";
import { FlexCenterWrapper } from "@/components/DefaultNoDataWrapper";
import { useNavigate } from "react-router-dom";
import { PROVIDER_PATHS, SMS_THREAD_PATIENT_PARAM } from "@/constants/paths";
import {
  ChatPatientRow,
  useChatPatientsList,
} from "@/queries/use-chat-patients";
import { formatTime } from "@/utils/date-time-utility";
import {
  DEF_PAGINATION,
  getInitials,
  // getLightBgColor,
} from "@/utils/utils";

export const DashMessagesBox = () => {
  const navigate = useNavigate();

  const { data: chatData, isFetching } = useChatPatientsList({
    pagination: { ...DEF_PAGINATION, pageSize: 20 },
    unreadOnly: true,
  });

  const patients: ChatPatientRow[] = chatData?.data?.content ?? [];
  const unreadCount = chatData?.data?.page?.totalElements ?? 0;

  return (
    <Flex flexDirection="column" height="100%" overflow="hidden" p={1}>
      <Flex justifyContent="space-between" alignItems="center" mb={1}>
        <CustomTitle
          sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "120%" }}
        >
          Messages
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              sx={{
                fontWeight: 600,
                fontSize: "11px",
                color: "common.black",
                backgroundColor: "info.light",
                height: "28px",
                minWidth: "28px",
                borderRadius: "50%",
                ml: "8px",
              }}
              size="small"
            />
          )}
        </CustomTitle>
        <Button
          variant="text"
          sx={{
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              transform: "translateY(-1px)",
            },
          }}
          onClick={() => navigate(PROVIDER_PATHS.MESSAGES)}
        >
          View All
        </Button>
      </Flex>

      <ScrollArea height="100%" overflow="auto" sx={{ flex: 1, minHeight: 0 }}>
        {isFetching && (
          <Flex justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Flex>
        )}

        {patients.length === 0 && !isFetching && (
          <FlexCenterWrapper>
            <Text>No new messages</Text>
          </FlexCenterWrapper>
        )}

        <Flex flexDirection="column">
          {patients.map((patient) => (
            <MessageRow
              key={patient.uuid}
              patient={patient}
              onClick={() =>
                navigate(
                  `${PROVIDER_PATHS.MESSAGES}?${SMS_THREAD_PATIENT_PARAM}=${patient.uuid}`
                )
              }
            />
          ))}
        </Flex>
      </ScrollArea>
    </Flex>
  );
};

const MessageRow = ({
  patient,
  onClick,
}: {
  patient: ChatPatientRow;
  onClick: () => void;
}) => {
  // const colors = getLightBgColor(patient.patientName || "");

  return (
    <Flex
      onClick={onClick}
      sx={{
        padding: "10px 4px",
        borderBottom: "1px solid #F0F0F0",
        cursor: "pointer",
        "&:hover": { backgroundColor: "#F9F9F9" },
        gap: 1.5,
        alignItems: "center",
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: "#E7E7E7",
          color: "#000000",
          fontSize: "16px",
          fontWeight: 400,
        }}
      >
        {getInitials(patient.patientName || "")}
      </Avatar>
      <Badge
        color="primary"
        badgeContent={""}
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "10px",
            minWidth: "12px",
            height: "12px",
            backgroundColor: "#049B22",
            color: "white",
            right: 19,
            top: 16,
          },
        }}
      />

      <Flex flexDirection="column" flex={1} minWidth={0}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text
            sx={{
              fontWeight: 400,
              fontSize: "14px",
              color: "#21262B",
            }}
          >
            {patient.patientName}
          </Text>
          <Text
            sx={{ fontSize: "11px", color: "#595F63", whiteSpace: "nowrap" }}
          >
            {patient.lastSmsTime ? formatTime(patient.lastSmsTime) : ""}
          </Text>
        </Flex>

        <Flex justifyContent="space-between" alignItems="center">
          <Text
            sx={{
              fontSize: "12px",
              color: "#104E84",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "180px",
              fontWeight: 400,
            }}
          >
            {patient.lastSms ||
              "lorem100 ipsum dolor sit amet, consectetur adipiscing elit."}
          </Text>
          {patient.unReadSmsCount > 0 && (
            <Badge
              badgeContent={patient.unReadSmsCount}
              color="primary"
              sx={{
                mr: 1,
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  minWidth: "18px",
                  height: "18px",
                  backgroundColor: "#104E84",
                  color: "white",
                },
              }}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
